// src/hooks/useProfileGateDraft.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { supabase } from "../supabaseClient";

const STORAGE_KEY_PREFIX = "profile_gate_draft_v7";
const LEGACY_V6_STORAGE_KEY_PREFIX = "profile_gate_draft_v6";
const LEGACY_SESSION_STORAGE_KEY = "profile_gate_draft_v5";
const LEGACY_LOCAL_STORAGE_KEY = "profile_gate_draft_v5";

const EMPTY_DRAFT = {
  // ---------------------------------------------------------
  // Account information
  // ---------------------------------------------------------
  full_name: "",
  mobile_number: "",
  province: "",
  notifications_enabled: null,

  // ---------------------------------------------------------
  // Existing Gwin business information
  // ---------------------------------------------------------
  app_reason: "",

  business_name: "",
  industry: "",

  business_age: "",
  monthly_revenue_range: "",

  legal_nature: "",
  registration_number: "",
  cipc_registered: null,

  // Legacy field.
  // Keep for backwards compatibility.
  address: "",

  // ---------------------------------------------------------
  // Business information
  // ---------------------------------------------------------
  employee_count_range: "",
  annual_revenue_range: "",
  registration_status: "",
  bank_accounts_separate: null,
  finance_tracking_method: "",

  // ---------------------------------------------------------
  // New onboarding v3 business fields
  //
  // These are text fields in business_profiles so the exact
  // learner response can be preserved without changing the
  // legacy PostgreSQL enums.
  // ---------------------------------------------------------
  business_setup_type: "",
  trading_duration_range: "",
  business_banking_type: "",

  // ---------------------------------------------------------
  // Learner profile
  // ---------------------------------------------------------
  business_priorities: [],

  confidence_cash_flow: "",
  confidence_profit_pricing: "",
  confidence_financial_numbers: "",
  confidence_funding_readiness: "",

  learning_days: [],
  reminder_times: [],

  // ---------------------------------------------------------
  // Durable onboarding progress
  // ---------------------------------------------------------
  resume_step: "",
  resume_updated_at: null,

  questionnaire_completed_at: null,
  orientation_completed_at: null,
  onboarding_completed_at: null,

  // ---------------------------------------------------------
  // Legacy Profile Gate fields
  // Keep temporarily for backwards compatibility.
  // ---------------------------------------------------------
  financial_challenges: [],
  main_financial_challenges: [],
  selected_challenges: [],
  financial_challenge: "",

  journey_stage: "",
  business_track: "",
  business_stage: "",
  track: "",
  recommended_track: "",

  profile_review_completed: false,

  // ---------------------------------------------------------
  // Legal consent
  // ---------------------------------------------------------
  accepted_terms: false,
  accepted_privacy_policy: false,
  accepted_cookies: false,
  accepted_popia: false,
  accepted_refund_cancellation: false,
  accepted_scoring_disclaimer: false,
  legal_consents_completed: false,
  legal_consents_accepted_at: null,
};

/* ==========================================================================
 * LEGACY OPTION LISTS
 * ==========================================================================
 *
 * Keep these exports for existing screens/components that may still import
 * them. The new onboarding uses the V3 constants further below.
 */

export const INDUSTRIES = [
  "Construction",
  "Services",
  "Food Services",
  "Health & Beauty",
  "Logistics",
  "Manufacturing",
  "Wholesale & Retail",
  "Other",
];

export const PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

/**
 * IMPORTANT:
 * These values match the existing PostgreSQL
 * revenue_range_label enum.
 */
export const REVENUE_RANGES = [
  "R0-R500k",
  "R500k-R1m",
  "R1m-R2.5m",
  "R2.5m-R5m",
  "R5m-R10m",
  "R10m-R25m",
  "R25m-R50m",
  "R50+",
];

/**
 * Existing newer onboarding values.
 * annual_revenue_range is a text field in business_profiles.
 */
export const ANNUAL_REVENUE_OPTIONS = [
  "Not earning yet",
  "Under R300k",
  "R300k–R1m",
  "R1m–R3m",
  "R3m–R10m",
  "R10m+",
];

/**
 * IMPORTANT:
 * These values match the existing PostgreSQL
 * business_age_label enum.
 */
export const BUSINESS_AGE_OPTIONS = [
  "Less than 6 months",
  "Less than 1 year",
  "1-2 years",
  "3-5 years",
  "6-10 years",
  "11-19 years",
  "20+ years",
];

export const LEGAL_NATURES = [
  "Company",
  "Sole Prop",
  "Close Corporation",
  "Non-Profit Organisation",
  "Partnership",
];

export const LEGAL_NATURES_REQUIRE_REG = [
  "Close Corporation",
  "Company",
  "Non-Profit Organisation",
];

export const REGISTRATION_STATUS_OPTIONS = [
  "Yes",
  "In progress",
  "No",
];

export const BUSINESS_PRIORITY_OPTIONS = [
  "Cash flow",
  "Profit & pricing",
  "Financial records",
  "Tax & compliance",
  "Understand my numbers",
  "Funding readiness",
];

/* ==========================================================================
 * ONBOARDING V3 — SCREEN 1
 * ========================================================================== */

/**
 * What would you like Gwin to help you with first?
 *
 * Single selection.
 * The selected answer will be stored as a one-item
 * business_priorities array.
 */
export const GWIN_FOCUS_OPTIONS = [
  "Take better control of my business money",
  "Build better financial habits",
  "Understand my business numbers better",
  "Get more organised for future funding",
  "I’m not sure yet",
];

/* ==========================================================================
 * ONBOARDING V3 — SCREEN 2
 * ========================================================================== */

export const BUSINESS_SETUP_OPTIONS = [
  "Pty Ltd",
  "Sole proprietor",
  "Partnership",
  "Other",
  "Not sure",
];

export const TRADING_DURATION_OPTIONS = [
  "Less than 6 months",
  "6–12 months",
  "1–3 years",
  "More than 3 years",
];

export const EMPLOYEE_COUNT_OPTIONS_V3 = [
  "Just me",
  "2–5",
  "6–10",
  "11+",
];

export const INDUSTRY_OPTIONS_V3 = [
  "Construction",
  "Professional Services",
  "Food Services",
  "Health and Beauty",
  "Logistics",
  "Manufacturing",
  "Wholesale and Retail",
  "Other",
];

/**
 * Keep the production province value consistent.
 *
 * The user sees "KZN".
 * Supabase stores "KwaZulu-Natal".
 */
export const PROVINCE_OPTIONS_V3 = [
  {
    label: "Western Cape",
    value: "Western Cape",
  },
  {
    label: "Eastern Cape",
    value: "Eastern Cape",
  },
  {
    label: "Free State",
    value: "Free State",
  },
  {
    label: "KZN",
    value: "KwaZulu-Natal",
  },
  {
    label: "Gauteng",
    value: "Gauteng",
  },
  {
    label: "Northern Cape",
    value: "Northern Cape",
  },
  {
    label: "Limpopo",
    value: "Limpopo",
  },
  {
    label: "Mpumalanga",
    value: "Mpumalanga",
  },
  {
    label: "North West",
    value: "North West",
  },
];

/* ==========================================================================
 * ONBOARDING V3 — SCREEN 3
 * ========================================================================== */

export const ANNUAL_REVENUE_OPTIONS_V3 = [
  "Not earning yet",
  "Under R300k",
  "R300k - R1m",
  "R1m - R3m",
  "R3m - R10m",
  "R10m+",
];

export const FINANCE_TRACKING_OPTIONS_V3 = [
  "Accountant or bookkeeper",
  "Accounting software",
  "Spreadsheet",
  "Notebook or manual records",
  "Bank account/statements",
  "I don’t have a regular system yet",
];

export const BUSINESS_BANKING_OPTIONS = [
  "I use a separate account for the business",
  "I use an account that also has personal money going through it",
  "I don’t currently use a bank account for the business",
];

/* ==========================================================================
 * ONBOARDING V3 — SCREEN 5
 * ========================================================================== */

/**
 * Exactly 3 must be selected by the learner.
 */
export const LEARNING_DAY_OPTIONS_V3 = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * Exactly 2 must be selected by the learner.
 */
export const CHECK_IN_TIME_OPTIONS = [
  "Morning",
  "Around lunchtime",
  "Afternoon",
  "Evening",
];

/* ==========================================================================
 * LOCAL DRAFT STORAGE
 * ========================================================================== */

function storageKeyForUser(userId) {
  return `${STORAGE_KEY_PREFIX}:${userId || "anonymous"}`;
}

function legacyV6StorageKeyForUser(userId) {
  return `${LEGACY_V6_STORAGE_KEY_PREFIX}:${userId || "anonymous"}`;
}

function safeJsonParse(raw) {
  try {
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function readStoredDraft(userId) {
  try {
    const currentDraft = safeJsonParse(
      localStorage.getItem(
        storageKeyForUser(userId)
      )
    );

    if (currentDraft) {
      return currentDraft;
    }
  } catch {}

  // Migrate v6 user-specific draft.
  try {
    const legacyV6Draft = safeJsonParse(
      localStorage.getItem(
        legacyV6StorageKeyForUser(userId)
      )
    );

    if (legacyV6Draft) {
      return legacyV6Draft;
    }
  } catch {}

  try {
    const legacyLocalDraft = safeJsonParse(
      localStorage.getItem(
        LEGACY_LOCAL_STORAGE_KEY
      )
    );

    if (legacyLocalDraft) {
      return legacyLocalDraft;
    }
  } catch {}

  try {
    const legacySessionDraft = safeJsonParse(
      sessionStorage.getItem(
        LEGACY_SESSION_STORAGE_KEY
      )
    );

    if (legacySessionDraft) {
      return legacySessionDraft;
    }
  } catch {}

  return null;
}

function writeStoredDraft(userId, draft) {
  try {
    localStorage.setItem(
      storageKeyForUser(userId),
      JSON.stringify({
        ...draft,
        _updatedAt: new Date().toISOString(),
      })
    );

    // Once v7 has been written, remove old draft versions.
    try {
      localStorage.removeItem(
        legacyV6StorageKeyForUser(userId)
      );

      localStorage.removeItem(
        LEGACY_LOCAL_STORAGE_KEY
      );

      sessionStorage.removeItem(
        LEGACY_SESSION_STORAGE_KEY
      );
    } catch {}
  } catch {}
}

function clearStoredDraft(userId) {
  try {
    localStorage.removeItem(
      storageKeyForUser(userId)
    );

    localStorage.removeItem(
      legacyV6StorageKeyForUser(userId)
    );

    localStorage.removeItem(
      LEGACY_LOCAL_STORAGE_KEY
    );

    sessionStorage.removeItem(
      LEGACY_SESSION_STORAGE_KEY
    );
  } catch {}
}

/* ==========================================================================
 * CLEANING / VALIDATION
 * ========================================================================== */

function hasPersonalFieldsComplete(source) {
  return Boolean(
    source?.full_name &&
      String(source.full_name).trim() &&
      source?.mobile_number &&
      String(source.mobile_number).trim()
  );
}

function cleanArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) =>
      String(item || "").trim()
    )
    .filter(Boolean);
}

function cleanBoolean(value) {
  return value === true;
}

function cleanNullableBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  return null;
}

function cleanText(value) {
  return String(value || "").trim();
}

function cleanDraft(source) {
  const draft =
    source &&
    typeof source === "object"
      ? source
      : {};

  return {
    ...EMPTY_DRAFT,

    // -------------------------------------------------------
    // Account
    // -------------------------------------------------------
    full_name: cleanText(
      draft.full_name
    ),

    mobile_number: cleanText(
      draft.mobile_number
    ),

    province: cleanText(
      draft.province
    ),

    notifications_enabled:
      cleanNullableBoolean(
        draft.notifications_enabled
      ),

    // -------------------------------------------------------
    // Existing business answers
    // -------------------------------------------------------
    app_reason: cleanText(
      draft.app_reason
    ),

    business_name: cleanText(
      draft.business_name
    ),

    industry: cleanText(
      draft.industry
    ),

    business_age: cleanText(
      draft.business_age
    ),

    monthly_revenue_range:
      cleanText(
        draft.monthly_revenue_range
      ),

    legal_nature: cleanText(
      draft.legal_nature
    ),

    registration_number:
      cleanText(
        draft.registration_number
      ),

    cipc_registered:
      cleanNullableBoolean(
        draft.cipc_registered
      ),

    address: cleanText(
      draft.address
    ),

    // -------------------------------------------------------
    // Current/new business answers
    // -------------------------------------------------------
    employee_count_range:
      cleanText(
        draft.employee_count_range
      ),

    annual_revenue_range:
      cleanText(
        draft.annual_revenue_range
      ),

    registration_status:
      cleanText(
        draft.registration_status
      ),

    bank_accounts_separate:
      cleanNullableBoolean(
        draft.bank_accounts_separate
      ),

    finance_tracking_method:
      cleanText(
        draft.finance_tracking_method
      ),

    business_setup_type:
      cleanText(
        draft.business_setup_type
      ),

    trading_duration_range:
      cleanText(
        draft.trading_duration_range
      ),

    business_banking_type:
      cleanText(
        draft.business_banking_type
      ),

    // -------------------------------------------------------
    // Learner profile
    // -------------------------------------------------------
    business_priorities:
      cleanArray(
        draft.business_priorities
      ),

    confidence_cash_flow:
      cleanText(
        draft.confidence_cash_flow
      ),

    confidence_profit_pricing:
      cleanText(
        draft.confidence_profit_pricing
      ),

    confidence_financial_numbers:
      cleanText(
        draft.confidence_financial_numbers
      ),

    confidence_funding_readiness:
      cleanText(
        draft.confidence_funding_readiness
      ),

    learning_days:
      cleanArray(
        draft.learning_days
      ),

    reminder_times:
      cleanArray(
        draft.reminder_times
      ),

    // -------------------------------------------------------
    // Progress
    // -------------------------------------------------------
    resume_step:
      cleanText(
        draft.resume_step
      ),

    resume_updated_at:
      draft.resume_updated_at ||
      null,

    questionnaire_completed_at:
      draft.questionnaire_completed_at ||
      null,

    orientation_completed_at:
      draft.orientation_completed_at ||
      null,

    onboarding_completed_at:
      draft.onboarding_completed_at ||
      null,

    // -------------------------------------------------------
    // Legacy Profile Gate
    // -------------------------------------------------------
    financial_challenges:
      cleanArray(
        draft.financial_challenges
      ),

    main_financial_challenges:
      cleanArray(
        draft.main_financial_challenges
      ),

    selected_challenges:
      cleanArray(
        draft.selected_challenges
      ),

    financial_challenge:
      cleanText(
        draft.financial_challenge
      ),

    journey_stage:
      cleanText(
        draft.journey_stage
      ),

    business_track:
      cleanText(
        draft.business_track
      ),

    business_stage:
      cleanText(
        draft.business_stage
      ),

    track:
      cleanText(
        draft.track
      ),

    recommended_track:
      cleanText(
        draft.recommended_track
      ),

    profile_review_completed:
      draft.profile_review_completed ===
      true,

    // -------------------------------------------------------
    // Legal
    // -------------------------------------------------------
    accepted_terms:
      cleanBoolean(
        draft.accepted_terms
      ),

    accepted_privacy_policy:
      cleanBoolean(
        draft.accepted_privacy_policy
      ),

    accepted_cookies:
      cleanBoolean(
        draft.accepted_cookies
      ),

    accepted_popia:
      cleanBoolean(
        draft.accepted_popia
      ),

    accepted_refund_cancellation:
      cleanBoolean(
        draft.accepted_refund_cancellation
      ),

    accepted_scoring_disclaimer:
      cleanBoolean(
        draft.accepted_scoring_disclaimer
      ),

    legal_consents_completed:
      cleanBoolean(
        draft.legal_consents_completed
      ),

    legal_consents_accepted_at:
      draft.legal_consents_accepted_at ||
      null,
  };
}

function consentValue(
  profileRow,
  stored,
  key
) {
  return Boolean(
    profileRow?.[key] ||
      stored?.[key]
  );
}

function getRegistrationStatus(
  businessRow
) {
  const explicitStatus =
    cleanText(
      businessRow?.registration_status
    );

  if (explicitStatus) {
    return explicitStatus;
  }

  if (
    businessRow?.cipc_registered ===
    true
  ) {
    return "Yes";
  }

  if (
    businessRow?.cipc_registered ===
    false
  ) {
    return "No";
  }

  return "";
}

export function hasAllLegalConsents(
  source
) {
  return Boolean(
    source?.accepted_terms &&
      source?.accepted_privacy_policy &&
      source?.accepted_cookies &&
      source?.accepted_popia &&
      source?.accepted_refund_cancellation &&
      source?.legal_consents_completed
  );
}

/* ==========================================================================
 * HOOK
 * ========================================================================== */

export function useProfileGateDraft() {
  const [
    draft,
    setDraft,
  ] = useState(
    EMPTY_DRAFT
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    savingLegalConsents,
    setSavingLegalConsents,
  ] = useState(false);

  const [
    hasPersonalComplete,
    setHasPersonalComplete,
  ] = useState(false);

  const [
    draftUserId,
    setDraftUserId,
  ] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const {
          data: { user },
          error: authError,
        } =
          await supabase.auth.getUser();

        if (authError) {
          throw authError;
        }

        const userId =
          user?.id || null;

        const metadata =
          user?.user_metadata || {};

        if (!alive) {
          return;
        }

        setDraftUserId(
          userId
        );

        const stored =
          readStoredDraft(
            userId
          ) || {};

        // ---------------------------------------------------
        // Not authenticated
        // ---------------------------------------------------
        if (!userId) {
          const fallback =
            cleanDraft({
              ...EMPTY_DRAFT,
              ...stored,
            });

          if (alive) {
            setDraft(
              fallback
            );

            setHasPersonalComplete(
              hasPersonalFieldsComplete(
                fallback
              )
            );

            writeStoredDraft(
              userId,
              fallback
            );

            setLoading(false);
          }

          return;
        }

        // ---------------------------------------------------
        // Load account + business + onboarding profile
        // ---------------------------------------------------
        const [
          profileResult,
          businessResult,
          onboardingResult,
        ] = await Promise.all([
          supabase
            .from("user")
            .select(
              [
                "full_name",
                "mobile_number",
                "email",
                "province",
                "notifications_enabled",
                "accepted_terms",
                "accepted_privacy_policy",
                "accepted_cookies",
                "accepted_popia",
                "accepted_refund_cancellation",
                "accepted_scoring_disclaimer",
                "legal_consents_completed",
                "legal_consents_accepted_at",
              ].join(", ")
            )
            .eq(
              "id",
              userId
            )
            .maybeSingle(),

          supabase
            .from(
              "business_profiles"
            )
            .select(
              [
                "app_reason",
                "business_name",
                "industry",
                "province",
                "business_age",
                "monthly_revenue_range",
                "annual_revenue_range",
                "legal_nature",
                "registration_number",
                "cipc_registered",
                "address",
                "employee_count_range",
                "registration_status",
                "bank_accounts_separate",
                "finance_tracking_method",

                // New onboarding v3
                "business_setup_type",
                "trading_duration_range",
                "business_banking_type",
              ].join(", ")
            )
            .eq(
              "user_id",
              userId
            )
            .maybeSingle(),

          supabase
            .from(
              "user_onboarding_profile"
            )
            .select(
              [
                "business_priorities",
                "confidence_cash_flow",
                "confidence_profit_pricing",
                "confidence_financial_numbers",
                "confidence_funding_readiness",
                "learning_days",
                "reminder_times",
                "resume_step",
                "resume_updated_at",
                "questionnaire_completed_at",
                "orientation_completed_at",
                "onboarding_completed_at",
              ].join(", ")
            )
            .eq(
              "user_id",
              userId
            )
            .maybeSingle(),
        ]);

        if (
          profileResult.error &&
          profileResult.error.code !==
            "PGRST116"
        ) {
          throw profileResult.error;
        }

        if (
          businessResult.error &&
          businessResult.error.code !==
            "PGRST116"
        ) {
          throw businessResult.error;
        }

        if (
          onboardingResult.error &&
          onboardingResult.error.code !==
            "PGRST116"
        ) {
          throw onboardingResult.error;
        }

        const profileRow =
          profileResult.data;

        const businessRow =
          businessResult.data;

        const onboardingRow =
          onboardingResult.data;

        const dbDraft =
          cleanDraft({
            // -------------------------------------------------
            // Account
            // -------------------------------------------------
            full_name:
              profileRow?.full_name ||
              metadata.full_name ||
              "",

            mobile_number:
              profileRow?.mobile_number ||
              metadata.mobile_number ||
              metadata.phone ||
              "",

            notifications_enabled:
              typeof profileRow
                ?.notifications_enabled ===
              "boolean"
                ? profileRow.notifications_enabled
                : null,

            // -------------------------------------------------
            // Existing business information
            // -------------------------------------------------
            app_reason:
              businessRow?.app_reason ||
              "",

            business_name:
              businessRow?.business_name ||
              "",

            industry:
              businessRow?.industry ||
              "",

            province:
              profileRow?.province ||
              businessRow?.province ||
              "",

            business_age:
              businessRow?.business_age ||
              "",

            monthly_revenue_range:
              businessRow
                ?.monthly_revenue_range ||
              "",

            annual_revenue_range:
              businessRow
                ?.annual_revenue_range ||
              "",

            legal_nature:
              businessRow?.legal_nature ||
              "",

            registration_number:
              businessRow
                ?.registration_number ||
              "",

            cipc_registered:
              typeof businessRow
                ?.cipc_registered ===
              "boolean"
                ? businessRow.cipc_registered
                : null,

            registration_status:
              getRegistrationStatus(
                businessRow
              ),

            address:
              businessRow?.address ||
              "",

            employee_count_range:
              businessRow
                ?.employee_count_range ||
              "",

            bank_accounts_separate:
              typeof businessRow
                ?.bank_accounts_separate ===
              "boolean"
                ? businessRow.bank_accounts_separate
                : null,

            finance_tracking_method:
              businessRow
                ?.finance_tracking_method ||
              "",

            // -------------------------------------------------
            // New onboarding v3 business fields
            // -------------------------------------------------
            business_setup_type:
              businessRow
                ?.business_setup_type ||
              "",

            trading_duration_range:
              businessRow
                ?.trading_duration_range ||
              "",

            business_banking_type:
              businessRow
                ?.business_banking_type ||
              "",

            // -------------------------------------------------
            // Learner onboarding profile
            // -------------------------------------------------
            business_priorities:
              onboardingRow
                ?.business_priorities ||
              [],

            confidence_cash_flow:
              onboardingRow
                ?.confidence_cash_flow ||
              "",

            confidence_profit_pricing:
              onboardingRow
                ?.confidence_profit_pricing ||
              "",

            confidence_financial_numbers:
              onboardingRow
                ?.confidence_financial_numbers ||
              "",

            confidence_funding_readiness:
              onboardingRow
                ?.confidence_funding_readiness ||
              "",

            learning_days:
              onboardingRow
                ?.learning_days ||
              [],

            reminder_times:
              onboardingRow
                ?.reminder_times ||
              [],

            // -------------------------------------------------
            // Progress
            // -------------------------------------------------
            resume_step:
              onboardingRow
                ?.resume_step ||
              "",

            resume_updated_at:
              onboardingRow
                ?.resume_updated_at ||
              null,

            questionnaire_completed_at:
              onboardingRow
                ?.questionnaire_completed_at ||
              null,

            orientation_completed_at:
              onboardingRow
                ?.orientation_completed_at ||
              null,

            onboarding_completed_at:
              onboardingRow
                ?.onboarding_completed_at ||
              null,

            // -------------------------------------------------
            // Legal
            // -------------------------------------------------
            accepted_terms:
              Boolean(
                profileRow?.accepted_terms
              ),

            accepted_privacy_policy:
              Boolean(
                profileRow
                  ?.accepted_privacy_policy
              ),

            accepted_cookies:
              Boolean(
                profileRow?.accepted_cookies
              ),

            accepted_popia:
              Boolean(
                profileRow?.accepted_popia
              ),

            accepted_refund_cancellation:
              Boolean(
                profileRow
                  ?.accepted_refund_cancellation
              ),

            accepted_scoring_disclaimer:
              Boolean(
                profileRow
                  ?.accepted_scoring_disclaimer
              ),

            legal_consents_completed:
              Boolean(
                profileRow
                  ?.legal_consents_completed
              ),

            legal_consents_accepted_at:
              profileRow
                ?.legal_consents_accepted_at ||
              null,
          });

        /**
         * Local draft wins while onboarding is in progress.
         *
         * Durable completion timestamps always come from
         * Supabase if they exist.
         */
        const next =
          cleanDraft({
            ...dbDraft,
            ...stored,

            accepted_terms:
              consentValue(
                profileRow,
                stored,
                "accepted_terms"
              ),

            accepted_privacy_policy:
              consentValue(
                profileRow,
                stored,
                "accepted_privacy_policy"
              ),

            accepted_cookies:
              consentValue(
                profileRow,
                stored,
                "accepted_cookies"
              ),

            accepted_popia:
              consentValue(
                profileRow,
                stored,
                "accepted_popia"
              ),

            accepted_refund_cancellation:
              consentValue(
                profileRow,
                stored,
                "accepted_refund_cancellation"
              ),

            accepted_scoring_disclaimer:
              consentValue(
                profileRow,
                stored,
                "accepted_scoring_disclaimer"
              ),

            legal_consents_completed:
              consentValue(
                profileRow,
                stored,
                "legal_consents_completed"
              ),

            legal_consents_accepted_at:
              profileRow
                ?.legal_consents_accepted_at ||
              stored
                .legal_consents_accepted_at ||
              null,

            questionnaire_completed_at:
              onboardingRow
                ?.questionnaire_completed_at ||
              stored
                .questionnaire_completed_at ||
              null,

            orientation_completed_at:
              onboardingRow
                ?.orientation_completed_at ||
              stored
                .orientation_completed_at ||
              null,

            onboarding_completed_at:
              onboardingRow
                ?.onboarding_completed_at ||
              stored
                .onboarding_completed_at ||
              null,
          });

        if (alive) {
          setDraft(
            next
          );

          setHasPersonalComplete(
            hasPersonalFieldsComplete(
              next
            )
          );

          writeStoredDraft(
            userId,
            next
          );

          setLoading(false);
        }
      } catch (error) {
        console.error(
          "Profile gate draft load error:",
          error
        );

        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ---------------------------------------------------------
  // Update one draft field
  // ---------------------------------------------------------
  const setField =
  useCallback(
    (field, value) => {
      setDraft((previous) => {
        const next = {
          ...previous,
          [field]:
            value,
        };

        writeStoredDraft(
          draftUserId,
          next
        );

        if (
          field ===
            "full_name" ||
          field ===
            "mobile_number"
        ) {
          setHasPersonalComplete(
            hasPersonalFieldsComplete(
              next
            )
          );
        }

        return next;
      });
    },
    [draftUserId]
  );

  // ---------------------------------------------------------
  // Update several draft fields
  // ---------------------------------------------------------
  const mergeFields =
    useCallback(
      (patch) => {
        const safePatch =
          patch &&
          typeof patch ===
            "object"
            ? patch
            : {};

        setDraft((previous) => {
          const next = {
  ...previous,
  ...safePatch,
};

          writeStoredDraft(
            draftUserId,
            next
          );

          if (
            "full_name" in
              safePatch ||
            "mobile_number" in
              safePatch
          ) {
            setHasPersonalComplete(
              hasPersonalFieldsComplete(
                next
              )
            );
          }

          return next;
        });
      },
      [draftUserId]
    );

  // ---------------------------------------------------------
  // Legal consent
  // ---------------------------------------------------------
  const saveLegalConsents =
    useCallback(async () => {
      setSavingLegalConsents(
        true
      );

      try {
        const {
          data: { user },
          error: userError,
        } =
          await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error(
            "No authenticated user found."
          );
        }

        const acceptedAt =
          new Date().toISOString();

        const consentPayload = {
          accepted_terms: true,
          accepted_privacy_policy:
            true,
          accepted_cookies: true,
          accepted_popia: true,
          accepted_refund_cancellation:
            true,
          legal_consents_completed:
            true,
          legal_consents_accepted_at:
            acceptedAt,
        };

        const {
          data: updatedRows,
          error: updateError,
        } =
          await supabase
            .from("user")
            .update(
              consentPayload
            )
            .eq(
              "id",
              user.id
            )
            .select("id");

        if (updateError) {
          throw updateError;
        }

        /**
         * Fallback for an account that somehow does not yet
         * have its public.user row.
         */
        if (
          !updatedRows ||
          updatedRows.length ===
            0
        ) {
          const fallbackFullName =
            draft.full_name ||
            user.user_metadata
              ?.full_name ||
            user.email
              ?.split("@")
              ?.[0] ||
            "Gwin User";

          const fallbackMobileNumber =
            draft.mobile_number ||
            user.user_metadata
              ?.mobile_number ||
            user.user_metadata
              ?.phone ||
            "";

          const {
            error:
              insertError,
          } =
            await supabase
              .from("user")
              .insert({
                id: user.id,
                email:
                  user.email,
                full_name:
                  fallbackFullName,
                mobile_number:
                  fallbackMobileNumber,
                ...consentPayload,
              });

          if (insertError) {
            throw insertError;
          }
        }

        setDraft(
          (previous) => {
            const next =
              cleanDraft({
                ...previous,
                ...consentPayload,
              });

            writeStoredDraft(
              user.id,
              next
            );

            return next;
          }
        );
      } finally {
        setSavingLegalConsents(
          false
        );
      }
    }, [
      draft.full_name,
      draft.mobile_number,
    ]);

  // ---------------------------------------------------------
  // Clear local onboarding draft
  // ---------------------------------------------------------
  const clearDraft =
    useCallback(() => {
      setDraft(
        EMPTY_DRAFT
      );

      setHasPersonalComplete(
        false
      );

      clearStoredDraft(
        draftUserId
      );
    }, [draftUserId]);

  // ---------------------------------------------------------
  // Legacy helper
  // ---------------------------------------------------------
  const requiresRegNumber =
    useMemo(
      () =>
        LEGAL_NATURES_REQUIRE_REG.includes(
          draft.legal_nature
        ),
      [
        draft.legal_nature,
      ]
    );

  return {
    loading,
    draft,
    setField,
    mergeFields,
    clearDraft,
    requiresRegNumber,
    hasPersonalComplete,
    savingLegalConsents,
    saveLegalConsents,
  };
}